import { useEffect, useRef, useState } from "react";

import { atom, useAtom } from "jotai";
import { CreditCard, MessageSquare, Store, User } from "lucide-react";
import { toast } from "sonner";

import { navigate, useLocation } from "@redwoodjs/router";

import {
  Breadcrumb,
  BreadcrumbItem,
  BreadcrumbList,
  BreadcrumbPage,
  BreadcrumbSeparator,
} from "src/components/ui/breadcrumb";
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogTitle,
} from "src/components/ui/dialog";
import {
  Drawer,
  DrawerContent,
  DrawerDescription,
  DrawerHeader,
  DrawerTitle,
} from "src/components/ui/drawer";
import { ScrollArea } from "src/components/ui/scroll-area";
import { storeIntegrationTypes } from "src/lib/constants";
import { trackPaymentMethodAdded } from "src/lib/googleAnalytics";
import { useApiMutation } from "src/lib/hooks/useApi";
import { useIsMobile } from "src/lib/hooks/useMobile";
import { CONFIRM_PAYMENT_METHOD_MUTATION } from "src/lib/queries/billing";
import { cn } from "src/lib/utils";

import AccountSettings from "./AccountSettings";
import BillingSettings from "./BillingSettings";
import NotificationSettings from "./NotificationSettings";
import StoreSettings from "./StoreSettings/StoreSettings";

export const settingsDialogAtom = atom({
  open: false,
  activeTab: "store",
  manageParam: null,
  sectionTarget: null,
  sectionTargetNonce: 0,
});

const tabs = [
  { id: "store", label: "Store", icon: Store },
  { id: "account", label: "Account", icon: User },
  { id: "billing", label: "Billing", icon: CreditCard },
  { id: "notifications", label: "Notifications", icon: MessageSquare },
];

const editableFieldSelector = [
  "input:not([type='button']):not([type='submit']):not([type='reset'])",
  "textarea",
  "select",
  "[contenteditable='true']",
].join(",");

const getFocusedEditableField = (root) => {
  const activeElement = root?.ownerDocument?.activeElement;

  if (
    !root ||
    !activeElement ||
    !root.contains(activeElement) ||
    typeof activeElement.matches !== "function" ||
    typeof activeElement.blur !== "function"
  ) {
    return null;
  }

  return activeElement.matches(editableFieldSelector) ? activeElement : null;
};

const SettingsDialog = () => {
  const [store, setStore] = useAtom(settingsDialogAtom);
  const [, setSelectedStoreId] = useAtom(
    storeIntegrationTypes.shopify.selectedAtom
  );
  const [preventClickOutside, setPreventClickOutside] = useState(false);
  const [highlightedSection, setHighlightedSection] = useState(null);
  const contentRootRef = useRef(null);
  const isMobile = useIsMobile();

  const { mutationApi } = useApiMutation({
    mutation: CONFIRM_PAYMENT_METHOD_MUTATION,
  });

  const { pathname, search, searchParams } = useLocation();

  useEffect(() => {
    const settingsSearch = searchParams.get("settings");
    let consumedParams = false;

    if (settingsSearch !== null) {
      const tab = tabs.find((t) => t.id === settingsSearch);
      const manageParam = searchParams.get("manage");
      const selectedStore = searchParams.get("store");
      if (selectedStore) {
        setSelectedStoreId(selectedStore);
      }
      setStore((store) => ({
        ...store,
        open: true,
        activeTab: tab ? tab.id : "store",
        manageParam,
        sectionTarget: null,
        sectionTargetNonce: 0,
      }));
      consumedParams = true;
    }

    const stripeSuccess = searchParams.get("stripeSetupSuccess");
    if (stripeSuccess !== null) {
      const processInvoices = async () => {
        const toastId = toast.loading("Adding payment method...");

        const { data } = await mutationApi();
        if (data?.confirmPaymentMethod) {
          trackPaymentMethodAdded();
          toast.success("Payment method successfully added!", { id: toastId });
        } else {
          toast.error("Error adding payment method", { id: toastId });
        }
      };

      setStore((store) => ({
        ...store,
        open: true,
        activeTab: "billing",
        manageParam: null,
        sectionTarget: null,
        sectionTargetNonce: 0,
      }));

      if (stripeSuccess === "true") {
        processInvoices();
      } else {
        toast.error("Error adding payment method");
      }

      consumedParams = true;
    }

    if (consumedParams) {
      const nextSearchParams = new URLSearchParams(search);
      nextSearchParams.delete("settings");
      nextSearchParams.delete("manage");
      nextSearchParams.delete("store");
      nextSearchParams.delete("stripeSetupSuccess");

      const nextSearch = nextSearchParams.toString();
      navigate(nextSearch ? `${pathname}?${nextSearch}` : pathname, {
        replace: true,
      });
    }
  }, [
    mutationApi,
    pathname,
    search,
    searchParams,
    setSelectedStoreId,
    setStore,
  ]);

  const { open, activeTab } = store;
  const setOpen = (open) => {
    setStore((prev) => ({
      ...prev,
      open,
      manageParam: open ? prev.manageParam : null,
      sectionTarget: open ? prev.sectionTarget : null,
      sectionTargetNonce: open ? prev.sectionTargetNonce : 0,
    }));
  };

  useEffect(() => {
    if (!open || !store.sectionTarget || !store.sectionTargetNonce) {
      return;
    }

    let animationFrameId = null;
    let nestedAnimationFrameId = null;
    let timeoutId = null;

    animationFrameId = window.requestAnimationFrame(() => {
      nestedAnimationFrameId = window.requestAnimationFrame(() => {
        const target = contentRootRef.current?.querySelector(
          `[data-settings-section="${store.sectionTarget}"]`
        );

        if (!target) {
          return;
        }

        setHighlightedSection(store.sectionTarget);
        target.scrollIntoView({
          behavior: "smooth",
          block: "center",
          inline: "nearest",
        });

        timeoutId = window.setTimeout(() => {
          setHighlightedSection((current) =>
            current === store.sectionTarget ? null : current
          );
        }, 1800);
      });
    });

    return () => {
      if (animationFrameId) {
        window.cancelAnimationFrame(animationFrameId);
      }
      if (nestedAnimationFrameId) {
        window.cancelAnimationFrame(nestedAnimationFrameId);
      }
      if (timeoutId) {
        window.clearTimeout(timeoutId);
      }
    };
  }, [activeTab, open, store.sectionTarget, store.sectionTargetNonce]);

  const clearManageParam = () => {
    setStore((prev) => ({
      ...prev,
      manageParam: null,
    }));
  };

  const handleInteractOutside = (event) => {
    if (preventClickOutside) {
      event.preventDefault();
      return;
    }

    const focusedEditableField = getFocusedEditableField(
      contentRootRef.current
    );

    if (!focusedEditableField) {
      return;
    }

    event.preventDefault();
    focusedEditableField.blur();
  };

  const renderContent = () => {
    switch (activeTab) {
      case "account":
        return (
          <AccountSettings
            manageParam={store.manageParam}
            sectionTarget={store.sectionTarget}
            highlightedSection={highlightedSection}
            clearManageParam={clearManageParam}
          />
        );
      case "store":
        return (
          <StoreSettings
            manageParam={store.manageParam}
            sectionTarget={store.sectionTarget}
            highlightedSection={highlightedSection}
            clearManageParam={clearManageParam}
            setPreventClickOutside={setPreventClickOutside}
          />
        );
      case "notifications":
        return <NotificationSettings />;
      case "billing":
        return <BillingSettings />;
      default:
        return null;
    }
  };

  const renderInnerContent = () => (
    <>
      <div
        className={cn(
          "border-b",
          isMobile
            ? "min-h-fit self-stretch overflow-x-auto"
            : "md:w-48 md:shrink-0 md:border-r md:border-b-0"
        )}
      >
        <nav
          className={cn(
            "flex gap-1 p-4",
            isMobile ? "flex-row overflow-x-auto" : "md:h-full md:flex-col"
          )}
        >
          {tabs.map((tab) => {
            const Icon = tab.icon;
            return (
              <button
                key={tab.id}
                onClick={() => {
                  setStore((prev) => ({
                    ...prev,
                    activeTab: tab.id,
                    manageParam: null,
                    sectionTarget: null,
                    sectionTargetNonce: 0,
                  }));
                }}
                className={cn(
                  "flex cursor-pointer items-center gap-2 rounded-md px-2 py-1.5 text-sm font-medium whitespace-nowrap transition-colors",
                  activeTab === tab.id
                    ? "bg-muted text-foreground"
                    : "text-muted-foreground hover:bg-muted hover:text-foreground"
                )}
              >
                <Icon className="h-4 w-4" />
                <span className={cn(isMobile ? "inline" : "hidden md:inline")}>
                  {tab.label}
                </span>
              </button>
            );
          })}
        </nav>
      </div>

      <div className="flex min-h-0 flex-1 flex-col pb-3">
        <div className="hidden border-b pt-4 pb-4 md:block">
          <Breadcrumb>
            <BreadcrumbList>
              <BreadcrumbItem>
                <BreadcrumbPage>Settings</BreadcrumbPage>
                <BreadcrumbSeparator />
              </BreadcrumbItem>
              <BreadcrumbItem>
                <BreadcrumbPage>
                  {tabs.find((t) => t.id === activeTab)?.label}
                </BreadcrumbPage>
              </BreadcrumbItem>
            </BreadcrumbList>
          </Breadcrumb>
        </div>

        <div className="flex-1 overflow-hidden">
          <ScrollArea className="h-full flex-1 py-4 pr-4 pl-3 md:pl-0">
            <div ref={contentRootRef} className="space-y-6 p-1">
              {renderContent()}
            </div>
          </ScrollArea>
        </div>
      </div>
    </>
  );

  if (isMobile) {
    return (
      <Drawer open={open} onOpenChange={setOpen}>
        <DrawerContent className="h-full max-h-[85vh]">
          <DrawerHeader className="sr-only">
            <DrawerTitle>Settings</DrawerTitle>
            <DrawerDescription>
              Manage account settings and preferences
            </DrawerDescription>
          </DrawerHeader>
          {renderInnerContent()}
        </DrawerContent>
      </Drawer>
    );
  }

  return (
    <Dialog
      open={open}
      onOpenChange={(state) => {
        setOpen(preventClickOutside || state);
      }}
    >
      <DialogContent
        className="flex h-[75dvh] max-w-4xl overflow-hidden rounded-lg p-0 sm:max-w-4xl"
        onOpenAutoFocus={(e) => e.preventDefault()}
        onInteractOutside={handleInteractOutside}
      >
        <DialogTitle className="sr-only">Settings</DialogTitle>
        <DialogDescription className="sr-only">
          Manage account settings and preferences
        </DialogDescription>
        {renderInnerContent()}
      </DialogContent>
    </Dialog>
  );
};

export default SettingsDialog;
